import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Box } from "@mui/material";
import "./App.css";
import Snackbar from "./components/snackbar";
import Signin from "./pages/signin";
import NavBar from "./components/navbar";
import Header from "./components/header";
import Dashboard from "./pages/dashboard";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import React from "react";
import PrivateRoute from "./components/PrivateRoute";
import "./api/axiosInterceptor";
import { noterActivite } from "./utilities/journalActivite";

import Location from "./pages/location";
import Bonus from "./pages/bonus";
import Achievements, { ONGLET_TIPS_OFFERTS } from "./pages/achievements";
import User_Location from "./pages/user_location";
import TipsRetraits from "./pages/TipsRetraits";
import User_Access from "./pages/user_access";
import Profile from "./pages/profile";
import Communications from "./pages/communications";
import ExtractData from "./pages/ExtractData";
import Produits from "./pages/produits";
import TypesLieu from "./pages/types_lieu";
import Pricing from "./pages/pricing";
import Forgotpassword from "./pages/forgotpassword";
import CreateQuest from "./pages/create_quest";
import Quests from "./pages/quests";
import QuestDetail from "./pages/quest_detail";
import Configuration from "./pages/configuration";
import SuperAdmin from "./pages/superadmin/SuperAdmin";
import Finance from "./pages/Finance";
import ClientsFactures from "./pages/facturation/ClientsFactures";
import ClientsInvites from "./pages/reseau/ClientsInvites";
import Dataviz from "./pages/Dataviz";
import TrousDonnees from "./pages/trous_donnees";
import Accueil from "./pages/accueil/Accueil";
import AFaire from "./pages/accueil/AFaire";
import QuetesAValider from "./pages/accueil/QuetesAValider";
import LieuxAValider from "./pages/lieux/LieuxAValider";
import PageDomaine from "./pages/PageDomaine";
import TitreSection from "./components/charte/TitreSection";
import { useAttente } from "./navigation/attente";
import {
  ANCIENNES_ADRESSES, Redirection, RedirectionConfiguration, RedirectionLieux, RedirectionQuete, estAncienneAdresse,
} from "./navigation/redirections";
import { AIDE_ACCUEIL, AIDE_ARGENT } from "./aide/textesAide";

function PrivateHome() {
  const navigate = useNavigate();
  const loggedIn = sessionStorage.getItem("isLoggedIn") === "true";
  useEffect(() => { navigate(loggedIn ? "/accueil" : "/sign_in", { replace: true }); }, [loggedIn]);
  return null;
}

/* Journal d'activité : une entrée par page ouverte, chemin seul (jamais la
   query string ni l'ancre, où vivent filtres et recherches). Hors session,
   rien n'est noté ; un échec du journal ne bloque pas la navigation. Une
   ancienne adresse n'est pas notée : elle ne fait que rediriger. */
function SuiviPages() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") return;
    if (["/", "/sign_in", "/forgotpassword"].includes(pathname)) return;
    if (estAncienneAdresse(pathname)) return;
    noterActivite("page", pathname);
  }, [pathname]);
  return null;
}

/* Page d'accueil d'« À traiter » : les décisions en attente, puis les pages. */
function ATraiter() {
  const attente = useAttente(true);
  return (
    <PageDomaine cle="traiter" avant={
      <Box component="section" sx={{ mb: 4 }}>
        <TitreSection titre="À faire maintenant"
          aide={AIDE_ACCUEIL.aFaire} />
        <AFaire attente={attente} />
      </Box>
    } />
  );
}

const P = (el: JSX.Element) => <PrivateRoute>{el}</PrivateRoute>;

function AppLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState<string>("");
  const isLoading = useSelector((state: any) => state.loading.isLoading);

  useEffect(() => {
    setIsAuthenticated(sessionStorage.getItem("isLoggedIn") ?? "");
  }, [isLoading]);

  const authenticated = isAuthenticated === "true";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "var(--c-bg)" }}>
      <Snackbar />
      <SuiviPages />

      {authenticated && <NavBar />}

      <Box
        component="main"
        sx={{
          flex: 1,
          ml: 0,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          background: "var(--c-bg)",
          color: "var(--c-text-primary)",
          position: "relative",
        }}
      >
        {authenticated && <Header />}

        <Box sx={{ flex: 1, p: 0, background: "transparent" }}>
          <Routes>
            <Route path="/" element={<PrivateHome />} />
            <Route path="/sign_in" element={<Signin />} />
            <Route path="/forgotpassword" element={<Forgotpassword />} />

            {/* ── Accueil ── */}
            <Route path="/accueil" element={P(<Accueil />)} />

            {/* ── À traiter ── */}
            <Route path="/a-traiter" element={P(<ATraiter />)} />
            <Route path="/a-traiter/lieux" element={P(<LieuxAValider vue="validation" />)} />
            <Route path="/a-traiter/quetes" element={P(<QuetesAValider />)} />

            {/* ── Quêtes ── */}
            <Route path="/quetes" element={P(<PageDomaine cle="quetes" />)} />
            <Route path="/quetes/liste" element={P(<Quests />)} />
            <Route path="/quetes/creer" element={P(<CreateQuest />)} />
            <Route path="/quetes/demandes-clients" element={P(<Quests sourceInitiale="client_ia" />)} />
            <Route path="/quetes/:id" element={P(<QuestDetail />)} />

            {/* ── Lieux ── */}
            <Route path="/lieux" element={P(<PageDomaine cle="lieux" />)} />
            <Route path="/lieux/fichier" element={P(<LieuxAValider vue="fichier" />)} />

            {/* ── Réseau ── */}
            <Route path="/reseau" element={P(<PageDomaine cle="reseau" />)} />
            <Route path="/reseau/spotters" element={P(<User_Location />)} />
            <Route path="/reseau/restrictions" element={P(<User_Access />)} />
            <Route path="/reseau/reputation" element={P(<Achievements />)} />
            {/* Tips offerts au palier : section de l'écran Niveaux & Badges (D9, option 3). */}
            <Route path="/reseau/reputation/tips-offerts" element={P(<Achievements ongletInitial={ONGLET_TIPS_OFFERTS} />)} />
            <Route path="/reseau/concours" element={P(<Bonus />)} />
            <Route path="/reseau/communications" element={P(<Communications />)} />
            <Route path="/reseau/clients-invites" element={P(<ClientsInvites />)} />

            {/* ── Argent ── */}
            <Route path="/argent" element={P(<PageDomaine cle="argent" />)} />
            <Route path="/argent/retraits" element={P(<TipsRetraits ongletInitial={1} />)} />
            <Route path="/argent/soldes" element={P(<TipsRetraits ongletInitial={0} />)} />
            <Route path="/argent/valeur-du-tip" element={P(<Configuration sections={["tips"]} domaine="argent" titre="Valeur du Tip et retraits"
              phrase="Valeur du Tip en F CFA, minimum et plafond de retrait, second avis. Réservé au super admin, motif obligatoire."
              aide={AIDE_ARGENT.valeurTip} />)} />
            <Route path="/argent/ventes" element={P(<Finance />)} />
            <Route path="/argent/clients" element={P(<ClientsFactures />)} />
            <Route path="/argent/facturation" element={P(<Configuration sections={["facturation"]} domaine="argent" titre="Facturation"
              phrase="Entités émettrices par pays, mentions légales, et archive des devis, contrats et factures émis."
              aide={AIDE_ARGENT.facturation} />)} />

            {/* ── Analyses ── */}
            <Route path="/analyses" element={P(<PageDomaine cle="analyses" />)} />
            <Route path="/analyses/carte" element={P(<Location />)} />
            <Route path="/analyses/collectes" element={P(<Dashboard />)} />
            <Route path="/analyses/trous" element={P(<TrousDonnees />)} />
            <Route path="/analyses/comparaison-des-sources" element={P(<Dataviz />)} />
            <Route path="/analyses/exports" element={P(<ExtractData />)} />

            {/* ── Réglages ── */}
            <Route path="/reglages" element={P(<PageDomaine cle="reglages" />)} />
            <Route path="/reglages/types-de-lieu" element={P(<TypesLieu />)} />
            <Route path="/reglages/produits" element={P(<Produits />)} />
            <Route path="/reglages/moteur-de-prix" element={P(<Pricing />)} />
            <Route path="/reglages/parametres" element={P(<Configuration sections={["freshness", "addmore", "vision", "integrite", "testeurs"]} />)} />
            {/* Z7-25 : une seule gestion des admins, l'onglet Admins de l'écran Équipe et comptes. */}
            <Route path="/reglages/equipe" element={<PrivateRoute superAdmin><SuperAdmin /></PrivateRoute>} />
            <Route path="/reglages/profil" element={P(<Profile />)} />

            {/* ── Anciennes adresses : redirection vers le nouvel emplacement ── */}
            {Object.entries(ANCIENNES_ADRESSES).map(([ancienne, nouvelle]) => (
              <Route key={ancienne} path={ancienne} element={<Redirection vers={nouvelle} />} />
            ))}
            <Route path="/location" element={<RedirectionLieux />} />
            <Route path="/configuration" element={<RedirectionConfiguration />} />
            <Route path="/freshness" element={<Navigate to="/reglages/parametres#freshness" replace />} />
            <Route path="/add_more" element={<Navigate to="/reglages/parametres#addmore" replace />} />
            <Route path="/quest/:id" element={<RedirectionQuete />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
